import { useAuth } from '@/contexts/AuthContext'
import { Bell, LogOut, User, Building2, ChevronDown } from 'lucide-react'
import { getInitials } from '@/lib/utils'
import { useState, useRef, useEffect } from 'react'

export function Header() {
  const { user, logout, availableTenants, switchTenant } = useAuth()
  const [showMenu, setShowMenu] = useState(false)
  const [showTenantMenu, setShowTenantMenu] = useState(false)
  const [switchingTo, setSwitchingTo] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const tenantMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
      if (tenantMenuRef.current && !tenantMenuRef.current.contains(event.target as Node)) {
        setShowTenantMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const otherTenants = availableTenants.filter(t => t.slug !== user?.tenant_slug)
  const currentTenantName = user?.tenant_name ?? user?.tenant_slug ?? '—'

  const handleSwitchTenant = async (slug: string) => {
    setSwitchingTo(slug)
    setShowTenantMenu(false)
    try {
      await switchTenant(slug)
    } finally {
      setSwitchingTo(null)
    }
  }

  return (
    <header className="flex h-16 items-center justify-between border-b bg-white px-6">
      <div className="flex items-center gap-4">
        <h2 className="text-lg font-semibold text-gray-900">Portal Admin</h2>
      </div>

      <div className="flex items-center gap-4">
        {/* ── Tenant switcher (only shown when user has multiple tenants) ── */}
        {otherTenants.length > 0 && (
          <div className="relative" ref={tenantMenuRef}>
            <button
              onClick={() => setShowTenantMenu(!showTenantMenu)}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
              title="Trocar de tenant"
            >
              <Building2 className="h-4 w-4 text-gray-500" />
              <span className="max-w-[120px] truncate font-medium">{currentTenantName}</span>
              <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
            </button>

            {showTenantMenu && (
              <div className="absolute right-0 mt-2 w-52 rounded-lg border bg-white py-1 shadow-lg z-50">
                <p className="px-3 py-1 text-xs font-semibold uppercase text-gray-400">Trocar para</p>
                {otherTenants.map(t => (
                  <button
                    key={t.slug}
                    onClick={() => handleSwitchTenant(t.slug)}
                    disabled={switchingTo === t.slug}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  >
                    <Building2 className="h-4 w-4 text-primary-500" />
                    {switchingTo === t.slug ? 'A trocar...' : t.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <button className="rounded-lg p-2 hover:bg-gray-100">
          <Bell className="h-5 w-5 text-gray-600" />
        </button>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="flex items-center gap-3 rounded-lg p-2 hover:bg-gray-100"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-sm font-medium text-white">
              {getInitials(user?.first_name, user?.last_name)}
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-2 w-56 rounded-lg border bg-white py-1 shadow-lg">
              <button
                onClick={() => {
                  setShowMenu(false)
                  window.location.href = '/settings/profile'
                }}
                className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <User className="h-4 w-4" />
                Perfil
              </button>
              <hr className="my-1" />
              <button
                onClick={() => {
                  setShowMenu(false)
                  logout()
                }}
                className="flex w-full items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
              >
                <LogOut className="h-4 w-4" />
                Terminar Sessão
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
